import { useState } from 'react';
import type { UserSummary } from '../lib/api';
import { Dialog } from './ui/Dialog';

interface Props {
  users: UserSummary[];
  // Users already known to be "involved" (assignee, reporter) — surfaced at top.
  suggestedUserIds: string[];
  onCancel: () => void;
  onSend: (userIds: string[], message: string | null) => Promise<void>;
}

/**
 * Modal for "Ping waiter" on a blocked ticket. Pick one or more teammates +
 * optional message; submit triggers an email + a system comment on the ticket.
 */
export function PingDialog({ users, suggestedUserIds, onCancel, onSend }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set(suggestedUserIds));
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const filtered = query
    ? users.filter((u) =>
        (u.name + ' ' + u.email).toLowerCase().includes(query.toLowerCase()),
      )
    : users;
  // Surface suggested users first.
  const sorted = filtered.slice().sort((a, b) => {
    const aS = suggestedUserIds.includes(a.id) ? 0 : 1;
    const bS = suggestedUserIds.includes(b.id) ? 0 : 1;
    return aS - bS;
  });

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit() {
    if (selected.size === 0 || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSend(Array.from(selected), message.trim() || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
      setSending(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => { if (!o && !sending) onCancel(); }}
      title="Request status update"
      description="Email a status-check to the person(s) blocking this ticket. A comment will also be posted on the ticket noting who you asked."
      footer={
        <>
          <button
            onClick={onCancel}
            disabled={sending}
            className="px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={() => void submit()}
            disabled={sending || selected.size === 0}
            className="px-3 py-1.5 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700 disabled:opacity-60"
          >
            {sending ? 'Sending…' : `Send request (${selected.size})`}
          </button>
        </>
      }
    >
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search users…"
        className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
      />
      <div className="border border-slate-200 rounded-md max-h-48 overflow-y-auto divide-y divide-slate-100">
        {sorted.length === 0 ? (
          <div className="px-3 py-4 text-sm text-slate-500 text-center">No matches</div>
        ) : (
          sorted.map((u) => (
            <label
              key={u.id}
              className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.has(u.id)}
                onChange={() => toggle(u.id)}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900 truncate">
                  {u.name}
                  {suggestedUserIds.includes(u.id) && (
                    <span className="ml-1.5 text-xs text-slate-500">(involved)</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
              </div>
            </label>
          ))
        )}
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-1">Message (optional)</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Any update on this? Customer is asking."
          rows={3}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
        />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </Dialog>
  );
}
